/**
 * Advisory Listeners
 *
 * The collision-proof listener manager for all three Strategic Advisory
 * channels (`engagements`, `goal`, `keyQuestions`). There is no fourth
 * `criteria` channel — no backend `criteria` subcollection has ever
 * existed for it to read.
 *
 * ## buildListenerKey collision proof
 *
 * 1. Every key begins with the literal `advisory::` followed by a `kind`
 *    drawn from a closed three-member union (`engagements`, `goal`,
 *    `keyQuestions`). No kind string is a prefix of another, so two keys
 *    with different kinds can never be equal.
 * 2. The `::` delimiter cannot occur inside a Firebase UID (`userId`, base62
 *    alphanumeric) or a client-minted `uuidv4` `projectId` (hex digits and
 *    hyphens only), so a delimiter can never be absorbed into an adjacent id
 *    segment to make two structurally different keys collapse to one
 *    string.
 * 3. Within a project-scoped kind, the key is a function of
 *    `(userId, projectId)` with the same delimiter guarantee, so two
 *    different projects or two different users never produce the same key.
 * 4. `buildListenerKey` is the ONLY function in this codebase permitted to
 *    construct one of these strings. No other line in this module may
 *    interpolate `advisory::` in a template literal — every listener key is
 *    built by calling this function, never by hand-writing one at a second
 *    call site (the exact trap `chatListeners.ts` still carries at six
 *    call sites, e.g. `` `conversations-${userId}` ``).
 */

import {
  extractErrorCode,
  fetchKeyQuestions,
  listenToEngagements,
  listenToGoalDoc,
  listenToKeyQuestions,
  mapEngagementSummaryDoc,
  mapGoalDoc,
  mapKeyQuestionDoc,
} from '../../../services/advisoryService';
import type { AdvisoryUnsubscribe } from '../../../services/advisoryService';
import { ADVISORY_MAPPER_ERROR_CODE } from './advisoryErrorCodes';
import { startLocalPoll } from './advisoryPolling';
import { deriveChannelState } from './advisoryStage';
import type {
  AdvisoryChannelStatus,
  AdvisoryListenerKind,
  ChannelState,
  EngagementSummaryDoc,
  KeyQuestionDoc,
  StrategicAdvisoryDoc,
} from './advisoryTypes';
import { WIRING_PLACEHOLDER_IDS } from './advisoryWiringPlaceholders';

/** Key parts for the `engagements` channel — it has no `projectId`. */
interface EngagementsListenerKeyParts {
  kind: 'engagements';
  userId: string;
}

/** Key parts for the two project-scoped channels. */
interface ProjectScopedListenerKeyParts {
  kind: 'goal' | 'keyQuestions';
  userId: string;
  projectId: string;
}

type AdvisoryListenerKeyParts = EngagementsListenerKeyParts | ProjectScopedListenerKeyParts;

/**
 * Builds the sole key any advisory listener is ever registered under. See
 * the module header for the four-point collision proof.
 *
 * Declared as two overload signatures over the two key-parts shapes above,
 * plus one implementation signature over their union. The overload
 * signatures are what make omitting `projectId` for a project-scoped kind,
 * or misspelling a kind, a COMPILE ERROR rather than a runtime throw —
 * TypeScript 4.9.5 supports plain function overloads and literal-union
 * discrimination, which is all this needs. The implementation signature
 * itself is never visible to a caller for overload resolution (TypeScript
 * only resolves calls against the two declared overloads above), but it
 * still carries `export` here: overload signatures are erased entirely at
 * compile time and produce no runtime code, so the implementation is the
 * only declaration that can actually export a callable `buildListenerKey`
 * — omitting `export` here would compile cleanly but silently produce a
 * module with no runtime `buildListenerKey` export at all.
 */
export function buildListenerKey(parts: EngagementsListenerKeyParts): string;
export function buildListenerKey(parts: ProjectScopedListenerKeyParts): string;
export function buildListenerKey(parts: AdvisoryListenerKeyParts): string {
  switch (parts.kind) {
    case 'engagements':
      return `advisory::engagements::${parts.userId}`;
    case 'goal':
    case 'keyQuestions':
      return `advisory::${parts.kind}::${parts.userId}::${parts.projectId}`;
    default: {
      // Exhaustiveness guard: a fifth kind added to AdvisoryListenerKeyParts
      // without a matching case above becomes a compile error here, rather
      // than a silent `undefined` return.
      const exhaustive: never = parts;
      return exhaustive;
    }
  }
}

/**
 * One registered advisory listener's bookkeeping: which kind it is, its
 * Firestore unsubscribe function, and its current per-channel health.
 * `status` is mutated in place by `markSnapshot`/`markError` below — never
 * replaced with a shared or frozen reference across entries.
 */
export interface AdvisoryListenerRecord {
  kind: AdvisoryListenerKind;
  unsubscribe: AdvisoryUnsubscribe;
  status: AdvisoryChannelStatus;
}

/**
 * The one `Map`-backed manager for all three advisory channels. Structure
 * copied verbatim from `ChatListenerManager`
 * (`chatListeners.ts:61-103` — `Map`, register-removes-then-sets, remove,
 * removeAll, count), with one addition: each entry carries its own
 * `AdvisoryChannelStatus`, so a denied or failed read on one channel can
 * never be mistaken for the health of another.
 */
class AdvisoryListenerManager {
  private listeners: Map<string, AdvisoryListenerRecord> = new Map();

  /**
   * Registers a listener under `key`, first removing (and unsubscribing)
   * any existing listener under the same key — the same "last write wins"
   * discipline `ChatListenerManager.addListener` uses, made safe here by
   * `buildListenerKey`'s collision-proof keying. The new entry always
   * starts with a fresh, unshared status object.
   */
  register(key: string, kind: AdvisoryListenerKind, unsubscribe: AdvisoryUnsubscribe): void {
    this.remove(key);
    this.listeners.set(key, {
      kind,
      unsubscribe,
      status: { firstSnapshotReceived: false, isEmpty: true, errorCode: null },
    });
  }

  /** Unsubscribes and removes the entry under `key`, if any. */
  remove(key: string): void {
    const entry = this.listeners.get(key);
    if (entry) {
      entry.unsubscribe();
      this.listeners.delete(key);
    }
  }

  /** Unsubscribes and removes every registered entry. */
  removeAll(): void {
    this.listeners.forEach(entry => entry.unsubscribe());
    this.listeners.clear();
  }

  /** The number of currently registered listeners. */
  getActiveListenerCount(): number {
    return this.listeners.size;
  }

  /** The raw entry for `key`, or `undefined` if nothing is registered under it. */
  getEntry(key: string): AdvisoryListenerRecord | undefined {
    return this.listeners.get(key);
  }

  /**
   * Records a successful snapshot: marks the first snapshot as received,
   * sets `isEmpty`, and clears any previously recorded error on this entry
   * only — a successful read is the one thing that can recover a channel
   * from `'denied'`/`'failed'` back to `'ready'`/`'empty'`.
   */
  markSnapshot(key: string, isEmpty: boolean): void {
    const entry = this.listeners.get(key);
    if (entry) {
      entry.status = { firstSnapshotReceived: true, isEmpty, errorCode: null };
    }
  }

  /** Records an error code on this entry only, leaving every other entry untouched. */
  markError(key: string, errorCode: string): void {
    const entry = this.listeners.get(key);
    if (entry) {
      entry.status = { ...entry.status, errorCode };
    }
  }

  /**
   * The entry's derived channel health, or `undefined` for an unregistered
   * key. Returning `undefined` rather than `'loading'` is deliberate: an
   * unknown key must be loud, never quietly indistinguishable from a
   * channel that is genuinely still loading.
   */
  getChannelState(key: string): ChannelState | undefined {
    const entry = this.listeners.get(key);
    return entry ? deriveChannelState(entry.status) : undefined;
  }
}

/** The singleton advisory listener manager. */
export const advisoryListenerManager = new AdvisoryListenerManager();

/**
 * Callback pair shared by all three listener kinds. The channel-state
 * callback is deliberately separate from the data callback — that
 * separation is what makes a denied read impossible to mistake for an
 * empty successful read (`conversationService.ts:292-295` folds them
 * together; this module does not repeat that).
 */
export interface AdvisoryChannelCallbacks<TData> {
  onData: (data: TData) => void;
  onChannelState: (state: ChannelState) => void;
}

interface EngagementsSetupParams extends AdvisoryChannelCallbacks<EngagementSummaryDoc[]> {
  userId: string;
}

interface ProjectScopedSetupParams<TData> extends AdvisoryChannelCallbacks<TData> {
  userId: string;
  projectId: string;
}

/** Reads the entry's current status and emits its derived state, if the entry still exists. */
function emitChannelState(key: string, onChannelState: (state: ChannelState) => void): void {
  const entry = advisoryListenerManager.getEntry(key);
  if (entry) {
    onChannelState(deriveChannelState(entry.status));
  }
}

/**
 * The ONE manager-level try/catch that guards every mapper invocation,
 * across all three listener kinds and every kind added later. A future
 * contributor cannot forget this guard because they never write it —
 * every setup function below routes its mapper call and its `onData` call
 * through this single helper instead of wrapping its own try/catch.
 *
 * On catch: the raw error goes to `console.error` for local debugging only
 * — it never crosses into any typed state a component could render, since
 * `AdvisoryChannelStatus` carries an error CODE only (`advisoryTypes.ts`).
 * The entry is marked with `ADVISORY_MAPPER_ERROR_CODE` and the resulting
 * derived state (`'failed'`) is emitted. This never rethrows — rethrowing
 * here would let a throw inside an `onSnapshot` callback silently kill the
 * whole listener, which is exactly the failure mode this guard exists to
 * close.
 */
function runGuardedMapping(
  key: string,
  onChannelState: (state: ChannelState) => void,
  work: () => void
): void {
  try {
    work();
  } catch (err) {
    console.error(`[advisoryListeners] mapper threw for ${key}:`, err);
    advisoryListenerManager.markError(key, ADVISORY_MAPPER_ERROR_CODE);
  }
  emitChannelState(key, onChannelState);
}

/**
 * Sets up the engagements-list listener for one user. Registers through
 * `advisoryListenerManager` under the key `buildListenerKey` builds — no
 * template literal is interpolated here.
 */
export function setupEngagementsListener(params: EngagementsSetupParams): void {
  const { userId, onData, onChannelState } = params;
  const key = buildListenerKey({ kind: 'engagements', userId });
  const unsubscribe = listenToEngagements(
    userId,
    rawDocs => {
      runGuardedMapping(key, onChannelState, () => {
        const mapped = rawDocs.map(mapEngagementSummaryDoc);
        advisoryListenerManager.markSnapshot(key, mapped.length === 0);
        onData(mapped);
      });
    },
    errorCode => {
      advisoryListenerManager.markError(key, errorCode);
      emitChannelState(key, onChannelState);
    }
  );
  advisoryListenerManager.register(key, 'engagements', unsubscribe);
}

/**
 * Sets up the goal-document listener for one project.
 */
export function setupGoalListener(
  params: ProjectScopedSetupParams<StrategicAdvisoryDoc | null> & { onDeleted?: () => void }
): void {
  const { userId, projectId, onData, onChannelState, onDeleted } = params;
  const key = buildListenerKey({ kind: 'goal', userId, projectId });
  const unsubscribe = listenToGoalDoc(
    userId,
    projectId,
    rawDoc => {
      runGuardedMapping(key, onChannelState, () => {
        const mapped = rawDoc === null ? null : mapGoalDoc(rawDoc);
        advisoryListenerManager.markSnapshot(key, mapped === null);
        onData(mapped);
      });
    },
    errorCode => {
      advisoryListenerManager.markError(key, errorCode);
      emitChannelState(key, onChannelState);
    },
    onDeleted
  );
  advisoryListenerManager.register(key, 'goal', unsubscribe);
}

/**
 * Key-question instantiation exists in agent-svc
 * (`ConversationQuestions.instantiate()`) but is called from nowhere
 * (`WIRING_PLACEHOLDER_IDS.keyQuestionInstantiation`), so every
 * key-questions listener's first snapshot is necessarily an empty array —
 * this channel always reports `'empty'` until that touchpoint is wired.
 */
export const KEY_QUESTIONS_LISTENER_PLACEHOLDER = WIRING_PLACEHOLDER_IDS.keyQuestionInstantiation;

/**
 * Sets up the key-questions-subcollection listener for one project.
 *
 * `keyQuestions` is user-mutable, so it gets a local-mode polling fallback
 * (the emulator's `onSnapshot` may not fire for a write from a separate
 * process, e.g. a live local agent-svc turn). `keyQuestions` is the only
 * channel carrying this fallback — there is no `criteria` channel to need
 * one, and `goal`/`engagements` deliberately keep plain `onSnapshot` (see
 * `advisoryPolling.ts`'s module header for that exposure).
 *
 * The env var read below is INSIDE the function body (never at module
 * scope — the established pattern in this codebase for a behavioral fork
 * that must be varied per-test), so a test can vary it without re-importing
 * this module. The single key-construction call above this branch is still
 * the only one in this function, and both branches register their
 * unsubscribe under that SAME key — every existing teardown path
 * (`cleanupKeyQuestionsListener`, `stopEngagementListeners`, cross-project
 * teardown, the logout teardown) keeps working with zero further wiring,
 * regardless of which branch produced the closure.
 */
export function setupKeyQuestionsListener(
  params: ProjectScopedSetupParams<KeyQuestionDoc[]>
): void {
  const { userId, projectId, onData, onChannelState } = params;
  const key = buildListenerKey({ kind: 'keyQuestions', userId, projectId });

  if (process.env.REACT_APP_ENVIRONMENT === 'local') {
    const unsubscribe = startLocalPoll({
      fetchFn: () => fetchKeyQuestions(userId, projectId),
      onDocs: rawDocs => {
        runGuardedMapping(key, onChannelState, () => {
          const mapped = rawDocs.map(mapKeyQuestionDoc);
          advisoryListenerManager.markSnapshot(key, mapped.length === 0);
          onData(mapped);
        });
      },
      onError: err => {
        advisoryListenerManager.markError(key, extractErrorCode(err));
        emitChannelState(key, onChannelState);
      },
    });
    advisoryListenerManager.register(key, 'keyQuestions', unsubscribe);
    return;
  }

  const unsubscribe = listenToKeyQuestions(
    userId,
    projectId,
    rawDocs => {
      runGuardedMapping(key, onChannelState, () => {
        const mapped = rawDocs.map(mapKeyQuestionDoc);
        advisoryListenerManager.markSnapshot(key, mapped.length === 0);
        onData(mapped);
      });
    },
    errorCode => {
      advisoryListenerManager.markError(key, errorCode);
      emitChannelState(key, onChannelState);
    }
  );
  advisoryListenerManager.register(key, 'keyQuestions', unsubscribe);
}

/** Cleans up the engagements-list listener for one user, if registered. */
export function cleanupEngagementsListener(userId: string): void {
  advisoryListenerManager.remove(buildListenerKey({ kind: 'engagements', userId }));
}

/** Cleans up the goal-document listener for one project, if registered. */
export function cleanupGoalListener(userId: string, projectId: string): void {
  advisoryListenerManager.remove(buildListenerKey({ kind: 'goal', userId, projectId }));
}

/** Cleans up the key-questions-subcollection listener for one project, if registered. */
export function cleanupKeyQuestionsListener(userId: string, projectId: string): void {
  advisoryListenerManager.remove(buildListenerKey({ kind: 'keyQuestions', userId, projectId }));
}

/**
 * Removes every registered advisory listener. Mirrors
 * `cleanupAllChatListeners`'s signature shape
 * (`gchat/src/redux/slices/chat/chatListeners.ts`), which has zero call
 * sites on `development` today. Called from `teardownAdvisorySession`
 * (`advisorySession.ts`), which fires from an auth-identity watcher rather
 * than `logoutUser.fulfilled` — see that module's own header — so a second
 * user in the same tab never receives the first user's snapshots.
 */
export function cleanupAllAdvisoryListeners(): void {
  advisoryListenerManager.removeAll();
}
